import type { DocumentsApiOptions } from '@/store/api/documentApi';
import type { Document as DocumentType } from '@/types';

import {
  useCreateDocumentMutation,
  useDeleteDocumentMutation,
  useGetDocumentsQuery,
  useUpdateDocumentMutation,
} from '@/store/api/documentApi';


interface UseGetDocumentsOptions {
  enabled?: boolean;
}

export const useGetDocuments = (
  params: DocumentsApiOptions,
  options?: UseGetDocumentsOptions,
) => {
  const result = useGetDocumentsQuery(params, {
    skip: options?.enabled === false,
  });

  return {
    ...result,
    data: result.data || { items: [], pages: 0, total: 0 },
  };
};

export const useCreateDocument = () => {
  const [trigger, result] = useCreateDocumentMutation();
  return {
    mutate: (
      variables: Parameters<typeof trigger>[0], 
      options?: { 
        onSuccess?: (data: DocumentType) => void; 
        onError?: (error: unknown) => void;
        onSettled?: () => void;
      }
    ) => {
      trigger(variables)
        .unwrap()
        .then((data) => {
          options?.onSuccess?.(data);
          options?.onSettled?.();
        })
        .catch((error) => {
          options?.onError?.(error);
          options?.onSettled?.();
        });
    },
    ...result,
  };
};

export const useDeleteDocument = () => {
  const [trigger, result] = useDeleteDocumentMutation();
  return {
    mutate: (
      variables: Parameters<typeof trigger>[0], 
      options?: { 
        onSuccess?: (data: void) => void; 
        onError?: (error: unknown) => void;
        onSettled?: () => void;
      }
    ) => {
      trigger(variables)
        .unwrap()
        .then((data) => {
          options?.onSuccess?.(data);
          options?.onSettled?.();
        })
        .catch((error) => {
          options?.onError?.(error);
          options?.onSettled?.();
        });
    },
    ...result,
  };
};

export const usePatchDocument = () => {
  const [trigger, result] = useUpdateDocumentMutation();
  return {
    mutate: (
      variables: Parameters<typeof trigger>[0], 
      options?: { 
        onSuccess?: (data: DocumentType) => void; 
        onError?: (error: unknown) => void;
        onSettled?: () => void;
      }
    ) => {
      trigger(variables)
        .unwrap()
        .then((data) => {
          options?.onSuccess?.(data);
          options?.onSettled?.();
        })
        .catch((error) => {
          options?.onError?.(error);
          options?.onSettled?.();
        });
    },
    ...result,
  };
};
